import Link from 'next/link';
import styled, { css } from 'styled-components';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import { Box } from '@/components/Box';
import { LegacyButton as BaseButton } from '@/components/LegacyButton';
import type { Maybe } from '@/lib/types';

import { ReactComponent as ChevronSvg } from '@/assets/icons/chevron.svg';
import { ReactComponent as OtherDirectionSvg } from '@/assets/icons/icon_other_direction.svg';

export const BoutiqueCardWrapper = styled(Box)`
  ${({ theme }) => css`
    margin-bottom: 15px;
    padding-left: 8px;
    padding-right: 8px;
    flex: 0 0 auto;
    flex-basis: 100%;
    max-width: 100%;

    ${theme.media.sm.up} {
      flex-basis: 50%;
      max-width: 50%;
    }
    ${theme.media.md.up} {
      flex-basis: 33.3333%;
      max-width: 33.3333%;
    }
  `}
`;
export const BoutiqueCardContent = styled(Box)`
  ${({ theme }) => css`
    background-color: ${theme.colors.lightGrey};
    height: 100%;
  `}
`;
export const BoutiqueImageWrapper = styled(Link)`
  cursor: pointer;
  ${({ theme }) => css`
    color: ${theme.colors.default};
  `}
  display: block;
  position: relative;
  overflow: hidden;
`;

export const StyledBoutiqueImage = styled(AspectRatioImage)`
  object-fit: cover;
  height: 100%;
  aspect-ratio: 16/9;
`;
export const InfoWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-basis: 100%;
  padding: 15px;
`;
export const NameAndTypeWrapper = styled(Box)`
  letter-spacing: 0;
  display: flex;
  align-items: center;
  min-width: 100%;
  ${({ theme }) => css`
    ${theme.media.sm.up} {
      min-height: 50px;
    }
  `}
`;
export const NameAndType = styled(Box)`
  width: 100%;
`;
export const NameAndDistanceWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
`;
export const DistanceWrapper = styled(Box)`
  display: flex;
  justify-content: flex-end;
`;
export const StyledDistance = styled(Box)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    color: ${theme.colors.default};
  `}
  line-height: 17px;
  margin-left: 4px;
  white-space: nowrap;
`;
export const Name = styled(Box)`
  ${({ theme }) => css`
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.p1};
    line-height: 19px;
    font-family: ${theme.fontFamilies.italianPlate};
    ${theme.media.md.up} {
      font-size: ${theme.fontSizes.h5};
      color: ${theme.colors.primaryBlue};
    }
  `}
  text-transform: uppercase;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  &:hover {
    -webkit-line-clamp: unset;
    overflow: unset;
    white-space: normal;
  }
`;

export const Type = styled(Box)`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
`;
export const TypeText = styled(Box)`
  text-transform: capitalize;
  font-weight: lighter;
  font-size: 12px;
  margin-right: 5px;
`;
export const OpenHoursWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
  min-width: 100%;
`;
export const OpenOrClose = styled(Box)`
  letter-spacing: 0;
  font-weight: 700;
  display: flex;
  ${({ theme }) => css`
    color: ${theme.colors.secondary};
    font-size: ${theme.fontSizes.p2};
  `}
  align-items: center;
  margin-right: 5px;
  margin-bottom: 8px;
`;
export const StyledSpan = styled.span<{ color?: Maybe<string> }>`
  font-weight: 700;
  ${({ color }) => css`
    background-color: ${color};
  `}
  margin-right: 5px;
  border-radius: 50%;
  height: 11px;
  max-width: 11px;
  min-width: 11px;
`;
export const Opens = styled(Box)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p2};
  `}
  display: flex;
  align-items: center;
  text-align: right;
  margin-bottom: 8px;
`;
export const AddressPhoneEmailWrapper = styled(Box)`
  font-size: 14px;
  line-height: 22px;
`;
export const StreetSpan = styled.div`
  text-transform: capitalize;
  min-height: 41px;
`;
export const StyledLink = styled.a`
  transition: all 0.2s ease-in-out;
  text-decoration: underline;
  ${({ theme }) => css`
    color: ${theme.colors.primaryBlue};
    &:hover {
      color: ${theme.colors.primary};
    }
  `}
`;
export const CtaWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  min-width: 100%;
`;
export const Button = styled(BaseButton)<{
  color?: string;
  lower?: boolean;
  underline?: boolean;
}>`
  text-decoration: none;
  ${({ lower }) =>
    lower &&
    css`
      text-transform: initial;
      font-weight: 500;
    `}
  ${({ underline }) =>
    underline &&
    css`
      text-decoration: underline;
      text-underline-offset: 5px;

      font-weight: 700;
      text-decoration-thickness: 1px;
    `}
`;
export const Chevron = styled(ChevronSvg)`
  fill: ${({ theme }) => theme.colors.primary};
`;

export const BookAppointmentCtaWrapper = styled(Box)`
  margin-top: 15px;
  min-height: 48px;
`;
export const StyledOtherDirectionSvg = styled(OtherDirectionSvg)`
  margin-top: 3px;
`;
